import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { toast } from "../../universal/toast";
import { CURRENCY, TIERS, TIER_PRICES } from "../../constants/membership";
import { toSettingsSectionPath } from "../account/accountSettingsNav";
import { closeLobbySettingsPanel } from "../../stores/lobbySettingsPanelStore";
import { SETTINGS_CONTENT_COLUMN_SX, MOBILE_NAV_MQ } from "./lobbySettingsStyles";

const PLAN_LOCKED_TOAST =
    "Your current plan cannot edit these lobby settings.";
const PLAN_LOCKED_TOAST_MS = 6500;
const CTA_CLIP = "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)";
const SUBSCRIPTIONS_PATH = toSettingsSectionPath("subscriptions");
const CURRENCY_SYMBOL = CURRENCY === "EUR" ? "\u20AC" : "$";
const PREMIUM_FROM_MONTHLY = TIER_PRICES[TIERS.PREMIUM].yearly;
const UNLOCKS = ["Playtime floors", "ELO spread", "Country limits"];

export default function PremiumOnlyOverlay({
    title = "Control who you queue with",
    description = "Keep smurfs, wide ELO gaps, and random regions out of your search.",
    plansHref = SUBSCRIPTIONS_PATH,
    upgradeButtonLabel = "Unlock with Premium",
}) {
    const handleLockedClick = (event) => {
        if (event.target.closest("a, button")) {
            return;
        }
        toast.error(PLAN_LOCKED_TOAST, { durationMs: PLAN_LOCKED_TOAST_MS });
    };

    return (
        <Box
            role="dialog"
            aria-label={title}
            onClick={handleLockedClick}
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                cursor: "pointer",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(8,8,10,0.12) 0%,
                        rgba(8,8,10,0.55) 42%,
                        rgba(5,5,7,0.94) 72%,
                        rgba(5,5,7,0.98) 100%
                    )
                `,
            }}
        >
            <Box
                sx={{
                    ...SETTINGS_CONTENT_COLUMN_SX,
                    pb: "1.15rem",
                    pt: "0.35rem",
                    [MOBILE_NAV_MQ]: {
                        pb: "0.95rem",
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: colors.primary.main,
                        mb: "0.4rem",
                    }}
                >
                    Premium
                </Typography>
                <Typography
                    sx={{
                        fontSize: { xs: "1.15rem", xl: "1.28rem" },
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        lineHeight: 1.15,
                        color: colors.text.primary,
                        mb: "0.4rem",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        lineHeight: 1.45,
                        color: "rgba(255,255,255,0.52)",
                        maxWidth: "26rem",
                        mb: "0.85rem",
                    }}
                >
                    {description}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.45rem",
                        mb: "1rem",
                    }}
                >
                    {UNLOCKS.map((label) => (
                        <Box
                            key={label}
                            sx={{
                                px: "0.55rem",
                                py: "0.28rem",
                                borderRadius: "4px",
                                border: `1px solid ${alpha(colors.primary.main, 0.28)}`,
                                background: alpha(colors.primary.main, 0.08),
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.64rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: colors.accent.soft,
                                    lineHeight: 1,
                                }}
                            >
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Button
                    component={Link}
                    to={plansHref}
                    onClick={closeLobbySettingsPanel}
                    variant="contained"
                    disableElevation
                    fullWidth
                    sx={{
                        minHeight: 34,
                        py: 0,
                        clipPath: CTA_CLIP,
                        WebkitClipPath: CTA_CLIP,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        letterSpacing: "0.03em",
                        color: colors.text.contrast,
                        background: colors.gradient.accent,
                        boxShadow: "none",
                        "&:hover": {
                            background: colors.gradient.accent,
                            filter: "brightness(1.05)",
                            boxShadow: "none",
                        },
                    }}
                    endIcon={
                        <ArrowForward sx={{ fontSize: "0.95rem !important" }} />
                    }
                >
                    {upgradeButtonLabel}
                </Button>
                <Typography
                    sx={{
                        mt: "0.55rem",
                        textAlign: "center",
                        fontSize: "0.68rem",
                        fontWeight: 500,
                        letterSpacing: "0.04em",
                        color: "rgba(255,255,255,0.38)",
                    }}
                >
                    From {CURRENCY_SYMBOL}
                    {Number(PREMIUM_FROM_MONTHLY).toFixed(2)}/mo on yearly
                </Typography>
            </Box>
        </Box>
    );
}
